/*
 * Generic quick-add popup, shared by every popup that snippets/quick-add-popup.liquid
 * renders on a page. Guarded so the duplicate <script> tags those snippets emit
 * execute the body exactly once; all handlers are delegated on document, so one
 * copy drives any number of popups.
 *
 * Built from promo-cover-popup.liquid, which had already solved the awkward
 * parts, with every promo rule removed: no date gate, no free-leg ratio, no
 * fabric-tier cap, no discount, no gift wording. Normal prices, normal cart.
 *
 * What was kept from the promo popup, and why:
 *   - material tabs built in JS from a data attribute on each tile, hidden when
 *     fewer than two materials survive, so a single-material product does not
 *     get a one-pill tab row;
 *   - tile images held in data-src and swapped to src on open, so a PDP with
 *     five popups does not download ~180 fabric thumbnails it may never show;
 *   - closing the cart drawer/notification if one is on screen, and again after
 *     350 ms, because Dawn opens the drawer asynchronously and it would
 *     otherwise land on top of the popup.
 *
 * What is new here:
 *   - the popup is moved to <body> on first open. It is authored inside the
 *     cross-sell <li>, and that rail is a horizontally scrolling, mask-image'd
 *     flex container. Rather than reason about which of those properties do or
 *     do not create a containing block for a fixed-position child in which
 *     browser, the element is reparented once and the question disappears.
 *   - focus trap, Escape, focus restored to the card that opened it;
 *   - add to cart goes through Dawn's own contract (FormData + sections +
 *     cart.renderContents), so the shopper gets the store's normal cart drawer
 *     rather than the promo popup's redirect to /cart.
 *
 * PRECEDENCE WITH THE PROMO POPUP (both can appear on one sofa PDP until
 * 7 September, when promo-cover-popup self-expires): the PROMO WINS.
 *   - open() refuses while #PromoCoverPopup is visible;
 *   - promo-cover-popup's show() calls window.FeyQuickAdd.close() first.
 * Two overlays therefore can never be on screen together. In practice the
 * promo only fires after a SOFA variant is added and this popup only ever adds
 * a cover, a cushion or a table, so the collision is rare, but it is closed by
 * construction rather than by luck.
 */
(function () {
  if (window.__qapWired) return;
  window.__qapWired = true;

  var openPopup = null;
  var lastFocus = null;

  function promoVisible() {
    var p = document.getElementById('PromoCoverPopup');
    return !!(p && !p.hidden);
  }

  function closeCartUI() {
    ['cart-drawer', 'cart-notification'].forEach(function (tag) {
      var el = document.querySelector(tag);
      if (el && typeof el.close === 'function') {
        try {
          el.close();
        } catch (e) {
          /* a drawer that was never opened throws; nothing to do */
        }
      }
    });
  }

  function tiles(pop) {
    return Array.prototype.slice.call(pop.querySelectorAll('[data-qap-add]'));
  }

  function select(pop, mat) {
    var tabs = pop.querySelector('.qap__tabs');
    tabs.querySelectorAll('.qap__tab').forEach(function (b) {
      var on = b.getAttribute('data-qap-tab') === mat;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    tiles(pop).forEach(function (t) {
      t.style.display = mat === null || t.getAttribute('data-qap-mat') === mat ? '' : 'none';
    });
  }

  function buildTabs(pop) {
    var tabs = pop.querySelector('.qap__tabs');
    if (!tabs || tabs.getAttribute('data-qap-built') === '1') return;
    tabs.setAttribute('data-qap-built', '1');
    var mats = [];
    tiles(pop).forEach(function (t) {
      var m = t.getAttribute('data-qap-mat');
      if (m && mats.indexOf(m) === -1) mats.push(m);
    });
    tabs.textContent = '';
    /* One material is not a choice: show every tile and no tab row. */
    if (mats.length < 2) {
      tabs.hidden = true;
      select(pop, null);
      return;
    }
    tabs.hidden = false;
    mats.forEach(function (m) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'qap__tab';
      b.setAttribute('role', 'tab');
      b.setAttribute('data-qap-tab', m);
      b.textContent = m;
      tabs.appendChild(b);
    });
    select(pop, mats[0]);
  }

  function focusable(pop) {
    return Array.prototype.filter.call(
      pop.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) {
        return !el.disabled && el.offsetWidth + el.offsetHeight > 0;
      }
    );
  }

  function open(id, trigger) {
    /* Promo first: it is the shop window for a discount that expires. */
    if (promoVisible()) return false;
    var pop = document.getElementById(id);
    if (!pop) return false;
    if (openPopup) close();
    if (pop.parentNode !== document.body) document.body.appendChild(pop);
    pop.querySelectorAll('img[data-src]').forEach(function (im) {
      im.src = im.getAttribute('data-src');
      im.removeAttribute('data-src');
    });
    buildTabs(pop);
    var err = pop.querySelector('.qap__err');
    if (err) err.hidden = true;
    lastFocus = trigger || document.activeElement;
    pop.hidden = false;
    document.documentElement.classList.add('qap-open');
    openPopup = pop;
    closeCartUI();
    setTimeout(closeCartUI, 350);
    var card = pop.querySelector('.qap__card');
    if (card) card.focus();
    return true;
  }

  function close() {
    if (!openPopup) return;
    openPopup.hidden = true;
    openPopup = null;
    document.documentElement.classList.remove('qap-open');
    var back = lastFocus;
    lastFocus = null;
    if (back && typeof back.focus === 'function') {
      try {
        back.focus();
      } catch (e) {
        /* the trigger can be gone if the section re-rendered */
      }
    }
  }

  function addToCart(tile) {
    var pop = tile.closest('.qap');
    if (!pop) return;
    var err = pop.querySelector('.qap__err');
    if (err) err.hidden = true;
    var all = tiles(pop);
    all.forEach(function (b) {
      b.disabled = true;
    });
    tile.classList.add('is-busy');

    var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    var body = new FormData();
    body.append('id', tile.getAttribute('data-qap-add'));
    if (Date.now() >= Date.parse('2026-11-02T00:00:00+01:00') &&
        Date.now() < Date.parse('2026-12-08T00:00:00+01:00')) {
      body.append('properties[_feydom_role]', 'extra-v1');
    }
    body.append('quantity', '1');
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.append(
        'sections',
        cart.getSectionsToRender().map(function (s) {
          return s.id;
        })
      );
      body.append('sections_url', window.location.pathname);
    }

    var config =
      typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/javascript' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = body;

    var url = (window.routes && window.routes.cart_add_url) || '/cart/add';
    fetch(url, config)
      .then(function (r) {
        return r.json();
      })
      .then(function (res) {
        /* Shopify answers a refused add with 422 and a `status` field. */
        if (res.status) throw new Error(res.description || res.message || 'add failed');
        /* Hand the drawer the element to return focus to before we close, the
           same handover product-form.js makes. */
        if (cart && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(lastFocus || document.body);
        }
        close();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'quick-add-popup',
            productVariantId: body.get('id'),
            cartData: res,
          });
        }
        if (cart) {
          cart.classList.remove('is-empty');
          cart.renderContents(res);
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }
      })
      .catch(function () {
        if (err) err.hidden = false;
      })
      .finally(function () {
        tile.classList.remove('is-busy');
        all.forEach(function (b) {
          /* never re-enable a tile that was disabled because it is sold out */
          b.disabled = b.hasAttribute('data-qap-soldout');
        });
      });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-qap-open]');
    if (trigger) {
      /* The card stays a real <a href> in the markup for crawlers and for a
         no-JS visit; the navigation is only cancelled once we know we can put
         a popup on screen instead. */
      if (open(trigger.getAttribute('data-qap-open'), trigger)) e.preventDefault();
      return;
    }
    if (!openPopup) return;
    if (e.target.closest('[data-qap-close]')) {
      e.preventDefault();
      close();
      return;
    }
    var tab = e.target.closest('[data-qap-tab]');
    if (tab && openPopup.contains(tab)) {
      select(openPopup, tab.getAttribute('data-qap-tab'));
      return;
    }
    var tile = e.target.closest('[data-qap-add]');
    if (tile && openPopup.contains(tile) && !tile.disabled) addToCart(tile);
  });

  document.addEventListener('keydown', function (e) {
    if (!openPopup) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var items = focusable(openPopup);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    var card = openPopup.querySelector('.qap__card');
    if (e.shiftKey && (document.activeElement === first || document.activeElement === card)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* promo-cover-popup.liquid calls close() before it shows itself. */
  window.FeyQuickAdd = {
    open: open,
    close: close,
    isOpen: function () {
      return !!openPopup;
    },
  };
})();
